import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getSystemAuditLogs, getUserAuditLogs, getActionTypes, getAuditLogPPDList, getAuditLogSchoolList } from '../../services/api';
import html2canvas from 'html2canvas';
import ReportIssueModal from '../StudentUniform/ReportIssueModal';
import Swal from 'sweetalert2';
import './SystemAuditLog.css';

const SystemAuditLog = () => {
    const { t } = useTranslation(['systemAuditLog', 'common']);
    const { user } = useAuth();

    // Tab state: 'user' for User Audit Log, 'system' for System Audit Log
    const [activeTab, setActiveTab] = useState('user');

    const [logs, setLogs] = useState([]);
    const [actionTypes, setActionTypes] = useState([]);
    const [ppdList, setPpdList] = useState([]);
    const [schoolList, setSchoolList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Filter states
    const [dateFrom, setDateFrom] = useState(() => {
        const today = new Date();
        return today.toISOString().split('T')[0]; // Default: TODAY
    });
    const [dateTo, setDateTo] = useState(() => {
        const today = new Date();
        return today.toISOString().split('T')[0]; // Default: TODAY
    });
    const [selectedPPD, setSelectedPPD] = useState('');
    const [selectedSchool, setSelectedSchool] = useState('');
    const [selectedActionType, setSelectedActionType] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    // Pagination states
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // Report Issue modal states
    const [showReportIssueModal, setShowReportIssueModal] = useState(false);
    const [screenshot, setScreenshot] = useState(null);

    // Load dropdown data and pre-select based on user role
    useEffect(() => {
        loadPPDList();
        loadSchoolList();

        // Pre-select user's own PPD and School for TEACHER/HEADMASTER
        if (user?.role === 'TEACHER' || user?.role === 'HEADMASTER') {
            setSelectedPPD(user.ppd || '');
            setSelectedSchool(user.schoolCode || '');
        }
        // Pre-select PPD for PPD role
        else if (user?.role === 'PPD') {
            setSelectedPPD(user.ppd || '');
        }
    }, [user]);

    // Load action types when tab changes
    useEffect(() => {
        loadActionTypes();
        setSelectedActionType(''); // Reset action type filter when tab changes
    }, [activeTab]);

    // Load logs when filters or tab change
    useEffect(() => {
        loadLogs();
    }, [page, pageSize, dateFrom, dateTo, selectedPPD, selectedSchool, selectedActionType, searchTerm, activeTab]);

    const loadActionTypes = async () => {
        try {
            // Use 'User' category for user tab, 'System' for system tab
            const category = activeTab === 'user' ? 'User' : 'System';
            const response = await getActionTypes(category);
            if (response.success && response.data) {
                setActionTypes(response.data);
            }
        } catch (err) {
            console.error('Error loading action types:', err);
        }
    };

    const loadPPDList = async () => {
        try {
            const response = await getAuditLogPPDList();
            if (response.success && response.data) {
                setPpdList(response.data);
            }
        } catch (err) {
            console.error('Error loading PPD list:', err);
        }
    };

    const loadSchoolList = async (ppdCode = null) => {
        try {
            const response = await getAuditLogSchoolList('SystemAuditLog', ppdCode);
            if (response.success && response.data) {
                setSchoolList(response.data);
            }
        } catch (err) {
            console.error('Error loading school list:', err);
        }
    };

    // Reload school list when PPD changes (for PPD/ADMIN/SUPERADMIN roles)
    useEffect(() => {
        if (user?.role === 'PPD' || user?.role === 'ADMIN' || user?.role === 'SUPERADMIN') {
            loadSchoolList(selectedPPD || null);
        }
    }, [selectedPPD, user]);

    const loadLogs = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const filters = {
                dateFrom,
                dateTo,
                ppdCode: selectedPPD || null,
                schoolCode: selectedSchool || null,
                actionType: selectedActionType || null,
                searchTerm: searchTerm || null,
                page,
                pageSize
            };

            // Call different API based on active tab
            const response = activeTab === 'user'
                ? await getUserAuditLogs(filters)
                : await getSystemAuditLogs(filters);

            if (response.success && response.data) {
                setLogs(response.data.data || []);
                setTotalCount(response.data.totalCount || 0);
                setTotalPages(response.data.totalPages || 0);
            } else {
                throw new Error(response.message || t('systemAuditLog:messages.error.loadFailed'));
            }
        } catch (err) {
            console.error('Error loading audit logs:', err);
            setError(err.message || t('systemAuditLog:messages.error.loadFailed'));
            setLogs([]);
        } finally {
            setLoading(false);
        }
    }, [dateFrom, dateTo, selectedPPD, selectedSchool, selectedActionType, searchTerm, page, pageSize, t, activeTab]);

    const handleSearch = () => {
        setPage(1); // Reset to first page
        loadLogs();
    };

    const handleReset = () => {
        const today = new Date().toISOString().split('T')[0];
        setDateFrom(today);
        setDateTo(today);
        setSelectedActionType('');
        setSearchTerm('');

        // Reset PPD and School based on user role
        if (user?.role === 'TEACHER' || user?.role === 'HEADMASTER') {
            setSelectedPPD(user.ppd || '');
            setSelectedSchool(user.schoolCode || '');
        } else if (user?.role === 'PPD') {
            setSelectedPPD(user.ppd || '');
            setSelectedSchool('');
        } else {
            setSelectedPPD('');
            setSelectedSchool('');
        }

        setPage(1);
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    const formatDateTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('en-MY', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        });
    };

    // Handle Report Issue button click
    const handleReportIssueClick = async () => {
        try {
            Swal.fire({
                title: t('systemAuditLog:messages.error.capturingScreenshot'),
                html: t('systemAuditLog:messages.error.pleaseWait'),
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const canvas = await html2canvas(document.body, {
                useCORS: true,
                allowTaint: true,
                scrollY: -window.scrollY,
                scrollX: -window.scrollX,
                width: window.innerWidth,
                height: window.innerHeight
            });

            const imageData = canvas.toDataURL('image/png');
            setScreenshot(imageData);
            setShowReportIssueModal(true);
            Swal.close();
        } catch (error) {
            console.error('Error capturing screenshot:', error);
            Swal.fire({
                icon: 'error',
                title: t('systemAuditLog:messages.error.captureFailed'),
                text: t('systemAuditLog:messages.error.captureFailedMessage')
            });
        }
    };

    // Determine if dropdowns should be disabled based on user role
    const isPPDDisabled = user?.role === 'TEACHER' || user?.role === 'HEADMASTER' || user?.role === 'PPD';
    const isSchoolDisabled = user?.role === 'TEACHER' || user?.role === 'HEADMASTER';

    return (
        <div className="system-audit-log-container">
            {/* Header with Logo and Title */}
            <div className="header-container">
                <img className="logo" src="/KTC.png" alt="KTC" />
                <h2 title={t('systemAuditLog:title', { lng: 'en' })}>
                    {t('systemAuditLog:title')} ({t('systemAuditLog:titleEn')})
                </h2>
                <button
                    className="header-btn btn-report-issue"
                    onClick={handleReportIssueClick}
                    title={t('systemAuditLog:tooltips.reportIssue')}
                >
                    <i className="fas fa-bug"></i>
                </button>
            </div>

            {/* Tab Navigation */}
            <div className="audit-log-tabs">
                <button
                    className={`tab-btn ${activeTab === 'user' ? 'active' : ''}`}
                    onClick={() => { setActiveTab('user'); setPage(1); }}
                    title={t('systemAuditLog:tabs.userAuditLog', { lng: 'en' })}
                >
                    <i className="fas fa-users"></i>
                    {t('systemAuditLog:tabs.userAuditLog')}
                </button>
                <button
                    className={`tab-btn ${activeTab === 'system' ? 'active' : ''}`}
                    onClick={() => { setActiveTab('system'); setPage(1); }}
                    title={t('systemAuditLog:tabs.systemAuditLog', { lng: 'en' })}
                >
                    <i className="fas fa-cogs"></i>
                    {t('systemAuditLog:tabs.systemAuditLog')}
                </button>
            </div>

            {/* Filter Panel */}
            <div className="filter-panel">
                <div className="filter-row">
                    <div className="filter-group">
                        <label title={t('systemAuditLog:filters.dateFrom', { lng: 'en' })}>
                            {t('systemAuditLog:filters.dateFrom')}:
                        </label>
                        <input
                            type="date"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            className="form-control"
                        />
                    </div>

                    <div className="filter-group">
                        <label title={t('systemAuditLog:filters.dateTo', { lng: 'en' })}>
                            {t('systemAuditLog:filters.dateTo')}:
                        </label>
                        <input
                            type="date"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            className="form-control"
                        />
                    </div>

                    <div className="filter-group">
                        <label title={t('systemAuditLog:filters.ppdDistrict', { lng: 'en' })}>
                            {t('systemAuditLog:filters.ppdDistrict')}:
                        </label>
                        <select
                            value={selectedPPD}
                            onChange={(e) => {
                                setSelectedPPD(e.target.value);
                                setSelectedSchool(''); // Reset school when PPD changes
                            }}
                            className="form-control"
                            disabled={isPPDDisabled}
                        >
                            <option value="">{t('systemAuditLog:filters.allDistricts')}</option>
                            {ppdList.map(ppd => (
                                <option key={ppd.code} value={ppd.code}>
                                    {ppd.code} - {ppd.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-group">
                        <label title={t('systemAuditLog:filters.school', { lng: 'en' })}>
                            {t('systemAuditLog:filters.school')}:
                        </label>
                        <select
                            value={selectedSchool}
                            onChange={(e) => setSelectedSchool(e.target.value)}
                            className="form-control"
                            disabled={isSchoolDisabled}
                        >
                            <option value="">{t('systemAuditLog:filters.allSchools')}</option>
                            {schoolList.map(school => (
                                <option key={school.schoolCode} value={school.schoolCode}>
                                    {school.schoolCode} - {school.schoolName}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="filter-row">
                    <div className="filter-group">
                        <label title={t('systemAuditLog:filters.actionType', { lng: 'en' })}>
                            {t('systemAuditLog:filters.actionType')}:
                        </label>
                        <select
                            value={selectedActionType}
                            onChange={(e) => setSelectedActionType(e.target.value)}
                            className="form-control"
                        >
                            <option value="">{t('systemAuditLog:filters.allActions')}</option>
                            {actionTypes.map(at => (
                                <option key={at.value} value={at.value}>
                                    {at.displayText}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-group">
                        <label title={t('systemAuditLog:filters.search', { lng: 'en' })}>
                            {t('systemAuditLog:filters.search')}:
                        </label>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder={t('systemAuditLog:placeholders.searchEmailName')}
                            className="form-control"
                            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                        />
                    </div>

                    <div className="filter-actions">
                        <button
                            onClick={handleSearch}
                            className="btn btn-primary"
                            title={t('systemAuditLog:buttons.search', { lng: 'en' })}
                        >
                            {t('systemAuditLog:buttons.search')}
                        </button>
                        <button
                            onClick={handleReset}
                            className="btn btn-secondary"
                            title={t('systemAuditLog:buttons.reset', { lng: 'en' })}
                        >
                            {t('systemAuditLog:buttons.reset')}
                        </button>
                    </div>

                    <div className="filter-group">
                        <label title={t('systemAuditLog:filters.pageSize', { lng: 'en' })}>
                            {t('systemAuditLog:filters.pageSize')}:
                        </label>
                        <select
                            value={pageSize}
                            onChange={(e) => {
                                setPageSize(Number(e.target.value));
                                setPage(1);
                            }}
                            className="form-control"
                        >
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                            <option value="200">200</option>
                        </select>
                    </div>

                    <div className="result-summary">
                        {t('systemAuditLog:pagination.showing')} {logs.length > 0 ? ((page - 1) * pageSize + 1) : 0} {t('systemAuditLog:pagination.to')} {Math.min(page * pageSize, totalCount)} {t('systemAuditLog:pagination.of')} {totalCount} {t('systemAuditLog:pagination.records')}
                    </div>
                </div>
            </div>

            {/* Results Table */}
            {loading ? (
                <div className="loading-spinner">
                    <div className="spinner"></div>
                    <p>{t('systemAuditLog:loading')}</p>
                </div>
            ) : error ? (
                <div className="error-message">
                    <i className="fas fa-exclamation-triangle"></i>
                    <p>{error}</p>
                    <button
                        onClick={loadLogs}
                        className="btn btn-primary"
                        title={t('systemAuditLog:buttons.retry', { lng: 'en' })}
                    >
                        {t('systemAuditLog:buttons.retry')}
                    </button>
                </div>
            ) : (
                <>
                    <div className="table-container">
                        <table className="audit-log-table">
                            <thead>
                                <tr>
                                    <th title={t('systemAuditLog:table.headers.dateTime', { lng: 'en' })}>
                                        {t('systemAuditLog:table.headers.dateTime')}
                                    </th>
                                    <th title={t('systemAuditLog:table.headers.action', { lng: 'en' })}>
                                        {t('systemAuditLog:table.headers.action')}
                                    </th>
                                    <th title={t('systemAuditLog:table.headers.targetUser', { lng: 'en' })}>
                                        {t('systemAuditLog:table.headers.targetUser')}
                                    </th>
                                    <th title={t('systemAuditLog:table.headers.targetRole', { lng: 'en' })}>
                                        {t('systemAuditLog:table.headers.targetRole')}
                                    </th>
                                    <th title={t('systemAuditLog:table.headers.performedBy', { lng: 'en' })}>
                                        {t('systemAuditLog:table.headers.performedBy')}
                                    </th>
                                    <th title={t('systemAuditLog:table.headers.byRole', { lng: 'en' })}>
                                        {t('systemAuditLog:table.headers.byRole')}
                                    </th>
                                    <th title={t('systemAuditLog:table.headers.school', { lng: 'en' })}>
                                        {t('systemAuditLog:table.headers.school')}
                                    </th>
                                    <th title={t('systemAuditLog:table.headers.details', { lng: 'en' })}>
                                        {t('systemAuditLog:table.headers.details')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.length === 0 ? (
                                    <tr>
                                        <td colSpan="8" className="no-data">
                                            {t('systemAuditLog:table.noData')}
                                        </td>
                                    </tr>
                                ) : (
                                    logs.map((log) => (
                                        <tr key={log.id}>
                                            <td className="datetime-column">
                                                {formatDateTime(log.timestamp)}
                                            </td>
                                            <td>
                                                <span className={`action-badge ${log.actionTypeBadgeClass}`}>
                                                    {log.actionTypeDisplay}
                                                </span>
                                            </td>
                                            <td>{log.userEmail || '-'}</td>
                                            <td>{log.targetRole || '-'}</td>
                                            <td>{log.performedBy}</td>
                                            <td>{log.performedByRole}</td>
                                            <td className="school-column">
                                                {log.schoolName ? (
                                                    <span title={log.schoolName}>
                                                        {log.schoolCode}
                                                    </span>
                                                ) : '-'}
                                            </td>
                                            <td className="details-column">
                                                {log.details ? (
                                                    <span className="details-text" title={log.details}>
                                                        {log.details.length > 100
                                                            ? log.details.substring(0, 100) + '...'
                                                            : log.details}
                                                    </span>
                                                ) : '-'}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="pagination-container">
                            <button
                                onClick={() => handlePageChange(page - 1)}
                                disabled={page === 1}
                                className="btn btn-secondary"
                                title={t('systemAuditLog:buttons.previous', { lng: 'en' })}
                            >
                                {t('systemAuditLog:buttons.previous')}
                            </button>

                            <div className="page-numbers">
                                {[...Array(Math.min(10, totalPages))].map((_, idx) => {
                                    let pageNum;
                                    if (totalPages <= 10) {
                                        pageNum = idx + 1;
                                    } else if (page <= 5) {
                                        pageNum = idx + 1;
                                    } else if (page >= totalPages - 4) {
                                        pageNum = totalPages - 9 + idx;
                                    } else {
                                        pageNum = page - 4 + idx;
                                    }

                                    return (
                                        <button
                                            key={pageNum}
                                            onClick={() => handlePageChange(pageNum)}
                                            className={`btn ${page === pageNum ? 'btn-primary' : 'btn-secondary'}`}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                })}
                            </div>

                            <button
                                onClick={() => handlePageChange(page + 1)}
                                disabled={page === totalPages}
                                className="btn btn-secondary"
                                title={t('systemAuditLog:buttons.next', { lng: 'en' })}
                            >
                                {t('systemAuditLog:buttons.next')}
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* Report Issue Modal */}
            {showReportIssueModal && (
                <ReportIssueModal
                    show={showReportIssueModal}
                    onClose={() => setShowReportIssueModal(false)}
                    screenshot={screenshot}
                    context={{
                        screen: t('systemAuditLog:context.screen'),
                        dateRange: { from: dateFrom, to: dateTo },
                        selectedPPD: selectedPPD,
                        selectedSchool: selectedSchool,
                        selectedActionType: selectedActionType,
                        searchTerm: searchTerm,
                        page: page,
                        pageSize: pageSize,
                        totalCount: totalCount
                    }}
                />
            )}
        </div>
    );
};

export default SystemAuditLog;
